import {
  ColorTheme,
  UserLogo,
  EntryType,
  EntryModel,
  WorkspaceEntryRef,
  EntryData,
  EntryPointer,
  UserProfile,
  ChatMessage,
  EntryRef,
} from "./models";

const BASE_API_URL = process.env.REACT_APP_API_BASE_URL;

async function fetchWrapper(data: {
  method: "GET" | "POST" | "PATCH" | "DELETE";
  endpoint: string;
  payload?: { [key: string]: any };
}): Promise<any> {
  const url = `${BASE_API_URL}/${data.endpoint}`;
  console.log(url);
  let requestInit: RequestInit = { method: data.method };
  if (data.payload) {
    const body = JSON.stringify(data.payload);
    requestInit = {
      ...requestInit,
      headers: { "Content-Type": "application/json" },
      body,
    };
  }
  return await fetch(url, requestInit);
}

function convertToPartialDict<T>(partial: Partial<T>): Record<string, any> {
  const partialDict: Record<string, any> = {};
  for (const key in partial) {
    if (partial[key] !== undefined) {
      partialDict[key] = partial[key];
    }
  }
  return partialDict;
}

export async function getUserProfile(): Promise<UserProfile> {
  const endpoint = `user_profile/`;
  const response = await fetchWrapper({ method: "GET", endpoint });
  return response.json();
}

interface UpdateableUserProfileFields {
  colorTheme?: ColorTheme;
  userLogo?: UserLogo;
  manifestation?: string;
}

export async function updateUserProfile(
  update: UpdateableUserProfileFields,
): Promise<UserProfile> {
  const endpoint = `user_profile/`;
  const response = await fetchWrapper({
    method: "POST",
    endpoint,
    payload: update,
  });
  return response.json();
}

export async function createEntry(
  entryType: EntryType,
  initial?: Partial<EntryData>,
): Promise<EntryModel> {
  const endpoint = `entries/${entryType}`;
  const response = await fetchWrapper({
    method: "POST",
    endpoint,
    payload: initial ? convertToPartialDict(initial) : undefined,
  });
  return response.json();
}

export async function updateEntry(
  uuid: string,
  entryType: EntryType,
  updated: Partial<EntryData>,
): Promise<EntryModel> {
  const endpoint = `entries/${entryType}/${uuid}`;
  const response = await fetchWrapper({
    method: "PATCH",
    endpoint,
    payload: convertToPartialDict(updated),
  });
  return await response.json();
}

export async function getEntry(
  uuid: string,
  entryType: EntryType,
): Promise<EntryModel> {
  const endpoint = `entries/${entryType}/${uuid}`;
  const response = await fetchWrapper({ method: "GET", endpoint });
  return await response.json();
}

export async function addEntryRef(
  containerType: EntryType,
  containerUuid: string,
  entryType: EntryType,
  entryUuid: string,
): Promise<EntryRef> {
  let params = `entry_uuid=${entryUuid}&entry_type=${entryType}`;
  const endpoint = `entry_ref/${containerType}/${containerUuid}/?${params}`;
  const response = await fetchWrapper({ method: "POST", endpoint });
  return response.json();
}

export async function addWorkspaceEntryRef(
  workspaceUuid: string,
  entryType: EntryType,
  entryUuid: string,
  x: number,
  y: number,
  zIndex: number,
): Promise<WorkspaceEntryRef> {
  let params = `entry_uuid=${entryUuid}&entry_type=${entryType}`;
  params += `&x=${x}&y=${y}&z_index=${zIndex}`;
  const endpoint = `workspace/${workspaceUuid}/entry_ref?${params}`;
  const response = await fetchWrapper({ method: "POST", endpoint });
  return response.json();
}

export async function updateWorkspaceEntryRef(
  workspaceUuid: string,
  refId: string,
  x: number,
  y: number,
  zIndex: number,
  width: number,
  height: number,
): Promise<WorkspaceEntryRef> {
  const params = `x=${x}&y=${y}&z_index=${zIndex}&width=${width}&height=${height}`;
  const endpoint = `workspace/${workspaceUuid}/entry_ref/${refId}?${params}`;
  const response = await fetchWrapper({ method: "PATCH", endpoint });
  return response.json();
}

export async function deleteWorkspaceEntryRef(
  workspaceUuid: string,
  refId: string,
): Promise<WorkspaceEntryRef> {
  const endpoint = `workspace/${workspaceUuid}/entry_ref/${refId}`;
  const response = await fetchWrapper({ method: "DELETE", endpoint });
  return response.json();
}

export async function queryEntries(query: string): Promise<EntryPointer[]> {
  const endpoint = `search/?query=${query}`;
  const response = await fetchWrapper({ method: "GET", endpoint });
  return response.json();
}

export async function loadChat(chatThreadId: string): Promise<ChatMessage[]> {
  const endpoint = `chat/${chatThreadId}`;
  const response = await fetchWrapper({ method: "GET", endpoint });
  return response.json();
}

export async function createNewChat(): Promise<string> {
  const endpoint = `chat/new`;
  const response = await fetchWrapper({ method: "GET", endpoint });
  return response;
}

export async function* chat(
  chatThreadId: string,
  message: string,
): AsyncGenerator<string, any, unknown> {
  const endpoint = `chat/${chatThreadId}?message=${message}`;
  const response = await fetchWrapper({ method: "POST", endpoint });

  if (!response.body) {
    throw new Error("Your browser does not support streaming responses.");
  }

  const reader: ReadableStreamDefaultReader<Uint8Array> =
    response.body.getReader();
  const decoder: TextDecoder = new TextDecoder();
  while (true) {
    const { done, value } = await reader.read();
    if (done) {
      break;
    }
    yield decoder.decode(value, { stream: true });
  }
}
